import type { Building, Category, Floor, Hotspot, Room, ThemeConfig, Tour } from '@/types/tour';

// Laedt die Tour-Daten zur Laufzeit. Drei Modi:
//
// 1) Builder-Modus mit Projekt-Param:
//    URL hat ?project=xyz  -> fetch via /backend/tour.php?project=xyz
//
// 2) Customer-Export-Modus:
//    URL hat keinen Projekt-Param und es liegt eine tour.json im Webroot
//    (das ist das ZIP-Export-Layout fuer Kunden-Hosting).
//
// 3) Builder-Modus ohne Projekt-Param:
//    URL hat keinen Projekt-Param und keine tour.json im Webroot.
//    -> NO_PROJECT_SELECTED, der Bootstrap zeigt die Projekt-Auswahl.

const TOUR_JSON_FILENAME = 'tour.json';
export const NO_PROJECT_SELECTED = 'NO_PROJECT_SELECTED';

export interface LoadedTour {
  tour: Tour;
  projectId: string | null;
}

export function getCurrentProjectId(): string | null {
  const params = new URLSearchParams(window.location.search);
  const id = params.get('project');
  if (!id) return null;
  return /^[a-z0-9-]+$/.test(id) ? id : null;
}

// Media-Base haengt vom Modus ab:
//   Builder mit ?project=tgz       -> "projects/tgz/"
//   Customer-Export (kein project) -> ""    (Pfade sind relativ zum Webroot)
export function getMediaBase(): string {
  const projectId = getCurrentProjectId();
  if (projectId) {
    return `${import.meta.env.BASE_URL}projects/${encodeURIComponent(projectId)}/`;
  }
  return import.meta.env.BASE_URL;
}

// Macht aus einem relativen Tour-Pfad eine voll aufgeloeste URL.
// Externe Demo-URLs (https://) und root-relative Pfade (/x.jpg) werden
// unveraendert durchgereicht.
export function mediaUrl(relativePath: string): string {
  if (!relativePath) return '';
  if (/^https?:\/\//i.test(relativePath)) return relativePath;
  if (relativePath.startsWith('/')) return relativePath;
  return getMediaBase() + relativePath.replace(/^\.\//, '');
}

export async function loadTour(): Promise<LoadedTour> {
  const projectId = getCurrentProjectId();
  if (projectId) {
    const url = `${import.meta.env.BASE_URL}backend/tour.php?project=${encodeURIComponent(projectId)}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      throw new Error(`tour.json fuer Projekt '${projectId}' konnte nicht geladen werden (HTTP ${res.status}).`);
    }
    return { tour: validateTour(await res.json()), projectId };
  }

  // Kein Projekt-Param: versuche statische tour.json (Customer-Export-Modus)
  const url = `${import.meta.env.BASE_URL}${TOUR_JSON_FILENAME}`;
  const res = await fetch(url, { cache: 'no-store' });
  if (res.ok) {
    return { tour: validateTour(await res.json()), projectId: null };
  }

  // Weder Projekt noch statische Tour: Builder-Home anzeigen
  throw new Error(NO_PROJECT_SELECTED);
}

// Strikte Validierung — wir scheitern frueh und mit klarer Meldung,
// wenn die JSON-Struktur kaputt ist (z.B. falscher Editor-Export).
export function validateTour(raw: unknown): Tour {
  if (!isObj(raw)) throw new Error('tour.json: Wurzel ist kein Objekt.');
  const meta = raw.meta;
  if (!isObj(meta) || typeof meta.title !== 'string') {
    throw new Error('tour.json: meta.title fehlt.');
  }
  if (!Array.isArray(raw.buildings)) {
    throw new Error('tour.json: buildings[] fehlt.');
  }
  return {
    meta: {
      title: meta.title,
      customer: typeof meta.customer === 'string' ? meta.customer : '',
      location: typeof meta.location === 'string' ? meta.location : '',
    },
    theme: isObj(raw.theme) ? validateTheme(raw.theme) : undefined,
    defaultRoomId: typeof raw.defaultRoomId === 'string' ? raw.defaultRoomId : undefined,
    categories: Array.isArray(raw.categories)
      ? raw.categories.map(validateCategory).filter((c): c is Category => c !== null)
      : undefined,
    buildings: raw.buildings.map((b, i) => validateBuilding(b, i)),
  };
}

function validateCategory(raw: unknown): Category | null {
  if (!isObj(raw)) return null;
  if (typeof raw.id !== 'string' || raw.id === '') return null;
  if (typeof raw.label !== 'string' || raw.label === '') return null;
  const cat: Category = { id: raw.id, label: raw.label };
  if (typeof raw.color === 'string') cat.color = raw.color;
  return cat;
}

// Theme-Felder werden nur uebernommen, wenn sie syntaktisch valide sind
// (Hex-Farbwerte fuer Farben, String fuer Pfade/Text). So koennen wir
// auch tour.json mit kaputten/aelteren Theme-Strukturen sicher laden.
type ColorKey = 'accent' | 'accentText' | 'bgPrimary' | 'bgSecondary' | 'bgTertiary'
  | 'textPrimary' | 'textSecondary' | 'textTertiary' | 'border';

function validateTheme(raw: Record<string, unknown>): ThemeConfig {
  const theme: ThemeConfig = {};
  const colorFields: ColorKey[] = [
    'accent', 'accentText', 'bgPrimary', 'bgSecondary', 'bgTertiary',
    'textPrimary', 'textSecondary', 'textTertiary', 'border',
  ];
  for (const k of colorFields) {
    const v = raw[k];
    if (typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v)) {
      theme[k] = v;
    }
  }
  if (typeof raw.logoUrl === 'string') theme.logoUrl = raw.logoUrl;
  if (typeof raw.logoScale === 'number' && raw.logoScale > 0 && raw.logoScale < 10) {
    theme.logoScale = raw.logoScale;
  }
  if (typeof raw.headerSubtitle === 'string') theme.headerSubtitle = raw.headerSubtitle;
  if (typeof raw.fontBody === 'string') theme.fontBody = raw.fontBody;
  if (typeof raw.fontMono === 'string') theme.fontMono = raw.fontMono;
  if (raw.layoutSidebar === 'left' || raw.layoutSidebar === 'right') {
    theme.layoutSidebar = raw.layoutSidebar;
  }
  if (
    raw.layoutOverlay === 'bottom-right' || raw.layoutOverlay === 'bottom-left' ||
    raw.layoutOverlay === 'top-right' || raw.layoutOverlay === 'top-left'
  ) {
    theme.layoutOverlay = raw.layoutOverlay;
  }
  if (typeof raw.customCss === 'string') theme.customCss = raw.customCss;
  if (isObj(raw.startScreen)) {
    const s = raw.startScreen;
    theme.startScreen = {
      enabled: s.enabled === true,
      title: typeof s.title === 'string' ? s.title : undefined,
      subtitle: typeof s.subtitle === 'string' ? s.subtitle : undefined,
      buttonText: typeof s.buttonText === 'string' ? s.buttonText : undefined,
      backgroundUrl: typeof s.backgroundUrl === 'string' ? s.backgroundUrl : undefined,
    };
  }
  return theme;
}

// Kuratierte Font-Auswahl. Die "stack"-Werte landen direkt in
// CSS-Custom-Properties; die WOFF2-Dateien muessen unter /fonts/<id>/
// vorliegen (siehe docs/FONTS.md). Fehlt eine Font-Datei, faellt der
// Browser auf den Stack-Fallback zurueck.
export interface FontOption {
  id: string;
  label: string;
  stack: string;
}

export const FONT_BODY_OPTIONS: FontOption[] = [
  { id: 'dm-sans', label: 'DM Sans', stack: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  { id: 'inter', label: 'Inter', stack: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  { id: 'manrope', label: 'Manrope', stack: '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  { id: 'system', label: 'System-Sans', stack: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' },
  { id: 'serif', label: 'System-Serif', stack: 'Georgia, "Times New Roman", Times, serif' },
];

export const FONT_MONO_OPTIONS: FontOption[] = [
  { id: 'space-mono', label: 'Space Mono', stack: '"Space Mono", ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace' },
  { id: 'jetbrains-mono', label: 'JetBrains Mono', stack: '"JetBrains Mono", ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace' },
  { id: 'system-mono', label: 'System-Mono', stack: 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace' },
];

function fontStackFor(id: string | undefined, options: FontOption[]): string | null {
  if (!id) return null;
  const opt = options.find((o) => o.id === id);
  return opt ? opt.stack : null;
}

// Mappt Theme-Felder auf CSS-Custom-Properties an :root. Wird von
// main.ts beim Bootstrap aufgerufen, und vom Editor live, wenn der
// User Farben aendert. Nicht-gesetzte Felder werden NICHT geclear,
// sondern fallen ueber die Default-Werte in _variables.scss zurueck.
export function applyTheme(theme: ThemeConfig | undefined): void {
  const root = document.documentElement;
  // Nur die direkt auf CSS-Custom-Props gemappten Farb-Felder hier;
  // Logo/Header/Layout/Font/CustomCss werden weiter unten anders gehandhabt.
  const colorMap: Record<ColorKey, string> = {
    accent: '--accent',
    accentText: '--accent-text',
    bgPrimary: '--bg-primary',
    bgSecondary: '--bg-secondary',
    bgTertiary: '--bg-tertiary',
    textPrimary: '--text-primary',
    textSecondary: '--text-secondary',
    textTertiary: '--text-tertiary',
    border: '--border',
  };
  for (const [key, cssVar] of Object.entries(colorMap) as [ColorKey, string][]) {
    const v = theme?.[key];
    if (typeof v === 'string') {
      root.style.setProperty(cssVar, v);
    } else {
      root.style.removeProperty(cssVar);
    }
  }
  if (theme?.accent) {
    root.style.setProperty('--accent-glow', hexToRgba(theme.accent, 0.2));
    root.style.setProperty('--accent-glow-subtle', hexToRgba(theme.accent, 0.08));
    root.style.setProperty('--accent-glow-strong', hexToRgba(theme.accent, 0.4));
    root.style.setProperty('--accent-dim', shade(theme.accent, -0.15));
  } else {
    root.style.removeProperty('--accent-glow');
    root.style.removeProperty('--accent-glow-subtle');
    root.style.removeProperty('--accent-glow-strong');
    root.style.removeProperty('--accent-dim');
  }

  // Overlay-Hintergruende leiten sich von bgPrimary ab — damit Info-
  // Overlay und Coords-Ticker zur gewaehlten Palette passen, statt
  // immer dem dunklen Petrol-Default.
  if (theme?.bgPrimary) {
    root.style.setProperty('--overlay-bg', hexToRgba(theme.bgPrimary, 0.7));
    root.style.setProperty('--overlay-bg-strong', hexToRgba(theme.bgPrimary, 0.85));
  } else {
    root.style.removeProperty('--overlay-bg');
    root.style.removeProperty('--overlay-bg-strong');
  }

  // Logo-Skalierung
  if (typeof theme?.logoScale === 'number' && theme.logoScale > 0) {
    root.style.setProperty('--logo-scale', String(theme.logoScale));
  } else {
    root.style.removeProperty('--logo-scale');
  }

  // Typografie
  const bodyStack = fontStackFor(theme?.fontBody, FONT_BODY_OPTIONS);
  const monoStack = fontStackFor(theme?.fontMono, FONT_MONO_OPTIONS);
  if (bodyStack) root.style.setProperty('--font-body', bodyStack);
  else root.style.removeProperty('--font-body');
  if (monoStack) root.style.setProperty('--font-mono', monoStack);
  else root.style.removeProperty('--font-mono');

  // Layout-Varianten als data-Attribute auf <body>
  const body = document.body;
  if (theme?.layoutSidebar === 'right') body.dataset.layoutSidebar = 'right';
  else delete body.dataset.layoutSidebar;
  if (theme?.layoutOverlay && theme.layoutOverlay !== 'bottom-right') {
    body.dataset.layoutOverlay = theme.layoutOverlay;
  } else {
    delete body.dataset.layoutOverlay;
  }

  // Custom-CSS (Stufe C) — wird in einen einzigen <style>-Tag mit
  // bekannter ID geschrieben, damit wir ihn bei Theme-Aenderungen
  // einfach ersetzen koennen.
  applyCustomCss(theme?.customCss);
}

const CUSTOM_CSS_STYLE_ID = 'theme-custom-css';

function applyCustomCss(css: string | undefined): void {
  let el = document.getElementById(CUSTOM_CSS_STYLE_ID) as HTMLStyleElement | null;
  if (!css) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = CUSTOM_CSS_STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = css;
}

// Hex -> rgba mit Alpha. Akzeptiert #rgb, #rrggbb, #rrggbbaa.
function hexToRgba(hex: string, alpha: number): string {
  const s = hex.replace('#', '');
  let r = 0, g = 0, b = 0;
  if (s.length === 3) {
    r = parseInt(s[0] + s[0], 16);
    g = parseInt(s[1] + s[1], 16);
    b = parseInt(s[2] + s[2], 16);
  } else if (s.length >= 6) {
    r = parseInt(s.slice(0, 2), 16);
    g = parseInt(s.slice(2, 4), 16);
    b = parseInt(s.slice(4, 6), 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Faerbt eine Hex-Farbe heller (positive amount) oder dunkler (negative).
// Sehr simpler Ansatz, reicht fuer accent-dim als Hover-Variante.
function shade(hex: string, amount: number): string {
  const s = hex.replace('#', '');
  if (s.length < 6) return hex;
  const ch = (i: number): string => {
    const v = parseInt(s.slice(i, i + 2), 16);
    const next = Math.max(0, Math.min(255, Math.round(v + 255 * amount)));
    return next.toString(16).padStart(2, '0');
  };
  return `#${ch(0)}${ch(2)}${ch(4)}`;
}

function validateBuilding(raw: unknown, idx: number): Building {
  if (!isObj(raw)) throw new Error(`buildings[${idx}]: kein Objekt.`);
  if (typeof raw.id !== 'string') throw new Error(`buildings[${idx}].id fehlt.`);
  if (!Array.isArray(raw.floors)) throw new Error(`buildings[${idx}].floors fehlt.`);
  return {
    id: raw.id,
    name: typeof raw.name === 'string' ? raw.name : raw.id,
    shortName: typeof raw.shortName === 'string' ? raw.shortName : raw.id,
    floors: raw.floors.map((f, j) => validateFloor(f, idx, j)),
  };
}

function validateFloor(raw: unknown, b: number, idx: number): Floor {
  if (!isObj(raw)) throw new Error(`buildings[${b}].floors[${idx}]: kein Objekt.`);
  if (typeof raw.id !== 'string') throw new Error(`floor.id fehlt.`);
  return {
    id: raw.id,
    name: typeof raw.name === 'string' ? raw.name : raw.id,
    shortName: typeof raw.shortName === 'string' ? raw.shortName : raw.id,
    floorplanSrc: typeof raw.floorplanSrc === 'string' ? raw.floorplanSrc : '',
    floorplanAspect:
      typeof raw.floorplanAspect === 'number' ? raw.floorplanAspect : undefined,
    rooms: Array.isArray(raw.rooms) ? raw.rooms.map(validateRoom) : [],
    hotspots: Array.isArray(raw.hotspots) ? raw.hotspots.map(validateHotspot) : [],
  };
}

function validateRoom(raw: unknown): Room {
  if (!isObj(raw)) throw new Error('Room: kein Objekt.');
  if (typeof raw.id !== 'string') throw new Error('room.id fehlt.');
  return {
    id: raw.id,
    name: typeof raw.name === 'string' ? raw.name : raw.id,
    meta: typeof raw.meta === 'string' ? raw.meta : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    tags: Array.isArray(raw.tags) ? raw.tags.filter((t): t is string => typeof t === 'string') : [],
    categoryId: typeof raw.categoryId === 'string' ? raw.categoryId : undefined,
    pano: typeof raw.pano === 'string' ? raw.pano : '',
    initialYaw: typeof raw.initialYaw === 'number' ? raw.initialYaw : 0,
    initialPitch: typeof raw.initialPitch === 'number' ? raw.initialPitch : undefined,
    initialZoom: typeof raw.initialZoom === 'number' ? raw.initialZoom : undefined,
  };
}

function validateHotspot(raw: unknown): Hotspot {
  if (!isObj(raw)) throw new Error('Hotspot: kein Objekt.');
  if (typeof raw.roomId !== 'string') throw new Error('hotspot.roomId fehlt.');
  return {
    roomId: raw.roomId,
    x: typeof raw.x === 'number' ? raw.x : 50,
    y: typeof raw.y === 'number' ? raw.y : 50,
    label: typeof raw.label === 'string' ? raw.label : undefined,
  };
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
